<template>
  <Admin>
    <v-main class="wrapper">
      <v-container fluid class="tw-px-6 tw-py-4">
        <!-- Header -->
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-6">
          <div class="tw-flex tw-items-center tw-gap-3">
            <v-icon large color="indigo darken-2">mdi-clipboard-check-outline</v-icon>
            <div>
              <h2 class="tw-text-xl tw-font-bold tw-m-0">Audit Automation</h2>
              <p class="tw-text-sm tw-text-gray-500 tw-m-0">
                อัปโหลดสรุปยอดของลูกค้าเพื่อเทียบกับเอกสารในระบบ (แยกตามเดือน)
              </p>
            </div>
          </div>
          <div v-if="hasResult" class="tw-flex tw-items-center tw-gap-2">
            <v-btn outlined color="indigo darken-2" @click="resetAll">
              <v-icon left small>mdi-refresh</v-icon> เริ่มใหม่
            </v-btn>
            <v-btn color="green darken-1" dark @click="exportExcel">
              <v-icon left small>mdi-file-excel</v-icon> Export Excel
            </v-btn>
          </div>
        </div>

        <!-- Upload zone -->
        <v-card v-if="!hasResult" class="tw-mb-6 tw-rounded-xl" outlined>
          <v-card-text class="tw-p-6">
            <div class="tw-flex tw-items-center tw-gap-4 tw-mb-4">
              <div class="tw-text-sm tw-font-semibold">รูปแบบไฟล์:</div>
              <v-btn-toggle v-model="fileType" mandatory dense>
                <v-btn value="excel" small><v-icon left small>mdi-file-excel</v-icon> Excel</v-btn>
                <v-btn value="pdf" small><v-icon left small>mdi-file-pdf-box</v-icon> PDF</v-btn>
              </v-btn-toggle>
              <span v-if="fileType === 'pdf'" class="tw-text-xs tw-text-orange-600">
                <v-icon x-small color="orange">mdi-information</v-icon>
                ของจริงต้องผ่าน OCR — ขณะนี้รองรับเฉพาะ Excel
              </span>
            </div>

            <div
              class="tw-border-2 tw-border-dashed tw-border-gray-300 tw-rounded-lg tw-p-10 tw-text-center tw-cursor-pointer hover:tw-border-indigo-400 hover:tw-bg-indigo-50 tw-transition"
              :class="{ 'tw-border-indigo-500 tw-bg-indigo-50': isDragging }"
              @click="$refs.fileInput.click()"
              @dragover.prevent="isDragging = true"
              @dragleave.prevent="isDragging = false"
              @drop.prevent="handleDrop"
            >
              <v-icon size="48" color="indigo darken-2">mdi-cloud-upload-outline</v-icon>
              <div class="tw-mt-3 tw-text-base tw-font-semibold">
                คลิกหรือลากไฟล์มาวางที่นี่
              </div>
              <div class="tw-text-xs tw-text-gray-500 tw-mt-1">
                {{ fileType === 'excel' ? 'รองรับไฟล์ .xlsx, .xls' : 'รองรับไฟล์ .pdf' }}
              </div>
              <input
                ref="fileInput"
                type="file"
                class="tw-hidden"
                :accept="fileType === 'excel' ? '.xlsx,.xls' : '.pdf'"
                @change="handleFilePick"
              />
            </div>

            <div v-if="parseError" class="tw-mt-4 tw-text-sm tw-text-red-600">
              <v-icon small color="red">mdi-alert-circle</v-icon>
              {{ parseError }}
            </div>
          </v-card-text>
        </v-card>

        <!-- Loading -->
        <div v-if="isProcessing" class="tw-flex tw-flex-col tw-items-center tw-py-16">
          <v-progress-circular indeterminate size="48" color="indigo darken-2" />
          <div class="tw-mt-3 tw-text-sm tw-text-gray-600">{{ processingStep }}</div>
        </div>

        <!-- Result -->
        <template v-if="hasResult && !isProcessing">
          <!-- Overview KPI -->
          <div class="tw-grid tw-grid-cols-5 tw-gap-3 tw-mb-6">
            <kpi-card icon="mdi-file-document-multiple" color="blue"
              :value="overall.uploadCount" label="เอกสารใน Upload" />
            <kpi-card icon="mdi-database" color="indigo"
              :value="overall.systemCount" label="เอกสารในระบบ" />
            <kpi-card icon="mdi-check-decagram" color="green"
              :value="overall.matchCount" label="ตรงกัน (Match)" />
            <kpi-card icon="mdi-alert" color="orange"
              :value="overall.mismatchCount" label="ไม่ตรง (Mismatch)" />
            <kpi-card icon="mdi-close-octagon" color="red"
              :value="overall.missingCount + overall.extraCount" label="ขาด/เกิน" />
          </div>

          <!-- Per-Month table -->
          <v-card class="tw-rounded-xl tw-mb-6" outlined>
            <v-card-title class="tw-text-base tw-font-bold">
              <v-icon left>mdi-calendar-month</v-icon>
              สรุปแยกรายเดือน
            </v-card-title>
            <v-divider />
            <v-simple-table>
              <thead>
                <tr class="tw-bg-gray-50">
                  <th>เดือน</th>
                  <th class="tw-text-right">Upload</th>
                  <th class="tw-text-right">ระบบ</th>
                  <th class="tw-text-right">Match</th>
                  <th class="tw-text-right">Mismatch</th>
                  <th class="tw-text-right">ขาดในระบบ</th>
                  <th class="tw-text-right">เกินในระบบ</th>
                  <th class="tw-text-center">สถานะ</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="m in monthlyRows"
                  :key="m.month"
                  class="tw-cursor-pointer hover:tw-bg-indigo-50"
                  @click="selectMonth(m.month)"
                >
                  <td class="tw-font-semibold">{{ formatMonth(m.month) }}</td>
                  <td class="tw-text-right">{{ m.uploadCount }}</td>
                  <td class="tw-text-right">{{ m.systemCount }}</td>
                  <td class="tw-text-right tw-text-green-600">{{ m.matchCount }}</td>
                  <td class="tw-text-right" :class="m.mismatchCount > 0 ? 'tw-text-orange-600 tw-font-bold' : ''">
                    {{ m.mismatchCount }}
                  </td>
                  <td class="tw-text-right" :class="m.missingCount > 0 ? 'tw-text-red-600 tw-font-bold' : ''">
                    {{ m.missingCount }}
                  </td>
                  <td class="tw-text-right" :class="m.extraCount > 0 ? 'tw-text-red-600 tw-font-bold' : ''">
                    {{ m.extraCount }}
                  </td>
                  <td class="tw-text-center">
                    <v-chip x-small :color="monthStatusColor(m)" text-color="white">
                      {{ monthStatusLabel(m) }}
                    </v-chip>
                  </td>
                  <td class="tw-text-right">
                    <v-icon small>mdi-chevron-right</v-icon>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card>
        </template>

        <!-- Detail dashboard dialog -->
        <v-dialog v-model="detailOpen" max-width="1500" scrollable>
          <v-card v-if="selectedMonth" class="detail-dashboard">
            <!-- Gradient header -->
            <div class="detail-header">
              <div class="detail-header-left">
                <v-icon size="36" color="white">mdi-calendar-month</v-icon>
                <div>
                  <div class="detail-month">{{ formatMonth(selectedMonth) }}</div>
                  <div class="detail-subtitle">รายงานการเปรียบเทียบเอกสาร · Audit Detail</div>
                </div>
              </div>
              <div class="detail-header-right">
                <v-btn outlined dark small @click="exportMonth(selectedMonth)" class="tw-mr-2">
                  <v-icon left small>mdi-file-excel</v-icon>
                  Export เดือนนี้
                </v-btn>
                <v-btn icon dark @click="detailOpen = false">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <v-card-text class="tw-p-0">
              <!-- Hero stats row -->
              <div class="hero-stats">
                <!-- Donut: match rate -->
                <div class="hero-donut">
                  <v-progress-circular
                    :rotate="-90"
                    :size="140"
                    :width="14"
                    :value="matchRate"
                    :color="matchRateColor"
                  >
                    <div class="tw-text-center">
                      <div class="hero-rate-num">{{ matchRate.toFixed(1) }}%</div>
                      <div class="hero-rate-label">Match Rate</div>
                    </div>
                  </v-progress-circular>
                  <div class="hero-rate-caption">
                    {{ detailStats.matchCount }} / {{ detailStats.uploadCount }} ตรงกัน
                  </div>
                </div>

                <!-- Stat tiles -->
                <div class="hero-tiles">
                  <div class="stat-tile stat-blue">
                    <div class="stat-icon-wrap"><v-icon color="white">mdi-file-upload-outline</v-icon></div>
                    <div>
                      <div class="stat-num">{{ detailStats.uploadCount }}</div>
                      <div class="stat-label">เอกสาร Upload</div>
                    </div>
                  </div>
                  <div class="stat-tile stat-indigo">
                    <div class="stat-icon-wrap"><v-icon color="white">mdi-database</v-icon></div>
                    <div>
                      <div class="stat-num">{{ detailStats.systemCount }}</div>
                      <div class="stat-label">เอกสารในระบบ</div>
                    </div>
                  </div>
                  <div class="stat-tile stat-green">
                    <div class="stat-icon-wrap"><v-icon color="white">mdi-check-decagram</v-icon></div>
                    <div>
                      <div class="stat-num">{{ detailStats.matchCount }}</div>
                      <div class="stat-label">Match</div>
                    </div>
                  </div>
                  <div class="stat-tile stat-orange">
                    <div class="stat-icon-wrap"><v-icon color="white">mdi-alert</v-icon></div>
                    <div>
                      <div class="stat-num">{{ detailStats.mismatchCount }}</div>
                      <div class="stat-label">Mismatch</div>
                    </div>
                  </div>
                  <div class="stat-tile stat-red">
                    <div class="stat-icon-wrap"><v-icon color="white">mdi-minus-circle</v-icon></div>
                    <div>
                      <div class="stat-num">{{ detailStats.missingCount }}</div>
                      <div class="stat-label">ขาดในระบบ</div>
                    </div>
                  </div>
                  <div class="stat-tile stat-amber">
                    <div class="stat-icon-wrap"><v-icon color="white">mdi-plus-circle</v-icon></div>
                    <div>
                      <div class="stat-num">{{ detailStats.extraCount }}</div>
                      <div class="stat-label">เกินในระบบ</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Amount reconciliation -->
              <div class="amount-reconcile">
                <div class="amount-block">
                  <div class="amount-block-head">
                    <v-icon small color="grey darken-1">mdi-cash-multiple</v-icon>
                    <span>มูลค่าก่อน VAT</span>
                  </div>
                  <div class="amount-pair">
                    <div class="amount-side">
                      <div class="amount-side-label">Upload</div>
                      <div class="amount-side-val">{{ formatNumber(amountTotals.uploadAmount) }}</div>
                    </div>
                    <v-icon class="amount-arrow">mdi-arrow-right</v-icon>
                    <div class="amount-side">
                      <div class="amount-side-label">ระบบ</div>
                      <div class="amount-side-val">{{ formatNumber(amountTotals.systemAmount) }}</div>
                    </div>
                    <div class="amount-delta" :class="deltaClass(amountTotals.amountDiff)">
                      <v-icon x-small>{{ deltaIcon(amountTotals.amountDiff) }}</v-icon>
                      Δ {{ formatNumber(Math.abs(amountTotals.amountDiff)) }}
                    </div>
                  </div>
                </div>
                <v-divider vertical />
                <div class="amount-block">
                  <div class="amount-block-head">
                    <v-icon small color="grey darken-1">mdi-percent-outline</v-icon>
                    <span>VAT</span>
                  </div>
                  <div class="amount-pair">
                    <div class="amount-side">
                      <div class="amount-side-label">Upload</div>
                      <div class="amount-side-val">{{ formatNumber(amountTotals.uploadVat) }}</div>
                    </div>
                    <v-icon class="amount-arrow">mdi-arrow-right</v-icon>
                    <div class="amount-side">
                      <div class="amount-side-label">ระบบ</div>
                      <div class="amount-side-val">{{ formatNumber(amountTotals.systemVat) }}</div>
                    </div>
                    <div class="amount-delta" :class="deltaClass(amountTotals.vatDiff)">
                      <v-icon x-small>{{ deltaIcon(amountTotals.vatDiff) }}</v-icon>
                      Δ {{ formatNumber(Math.abs(amountTotals.vatDiff)) }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Filter pills -->
              <div class="filter-pills">
                <v-chip
                  v-for="(t, i) in tabConfig"
                  :key="t.key"
                  :outlined="detailTab !== i"
                  :color="t.color"
                  :text-color="detailTab === i ? 'white' : t.color"
                  class="tw-mr-2 tw-mb-2 filter-chip"
                  @click="detailTab = i"
                >
                  <v-icon left small>{{ t.icon }}</v-icon>
                  {{ t.label }}
                  <span class="filter-chip-count">{{ detailRows[t.key].length }}</span>
                </v-chip>
              </div>

              <!-- Body -->
              <div class="detail-body">
                <!-- Empty -->
                <div v-if="currentTabRows.length === 0" class="empty-state">
                  <v-icon size="64" color="grey lighten-2">mdi-checkbox-marked-circle-outline</v-icon>
                  <div class="tw-mt-3 tw-text-gray-400">ไม่มีข้อมูลในหมวดนี้</div>
                </div>

                <!-- Mismatch: comparison cards -->
                <template v-else-if="currentTabKey === 'mismatch'">
                  <div
                    v-for="(row, idx) in currentTabRows"
                    :key="`mm-${idx}`"
                    class="mismatch-card"
                  >
                    <div class="mismatch-card-head">
                      <div class="tw-flex tw-items-center tw-gap-2">
                        <v-icon color="orange darken-2">mdi-alert-circle</v-icon>
                        <span class="mismatch-docno">{{ row.upload.docId }}</span>
                        <v-chip x-small color="orange darken-2" text-color="white">
                          {{ row.diffs.length }} ฟิลด์ไม่ตรง
                        </v-chip>
                      </div>
                      <div class="tw-text-xs tw-text-gray-500">
                        {{ formatDate(row.upload.docDate) }} · {{ row.upload.cardName }}
                      </div>
                    </div>
                    <div class="diff-grid">
                      <div class="diff-head diff-head-empty"></div>
                      <div class="diff-head diff-head-upload">
                        <v-icon x-small color="blue darken-2">mdi-file-upload</v-icon> Upload (User)
                      </div>
                      <div class="diff-head diff-head-system">
                        <v-icon x-small color="indigo darken-2">mdi-database</v-icon> ระบบ
                      </div>
                      <div class="diff-head diff-head-delta">Δ</div>

                      <template v-for="d in row.diffs">
                        <div :key="`f-${d.field}`" class="diff-cell diff-cell-label">
                          {{ fieldLabel(d.field) }}
                        </div>
                        <div :key="`u-${d.field}`" class="diff-cell diff-cell-upload">
                          {{ formatField(d.field, d.upload) }}
                        </div>
                        <div :key="`s-${d.field}`" class="diff-cell diff-cell-system">
                          {{ formatField(d.field, d.system) }}
                        </div>
                        <div :key="`d-${d.field}`" class="diff-cell diff-cell-delta">
                          <span v-if="isNumericField(d.field)" :class="deltaClass(numericDelta(d))">
                            {{ formatNumberSigned(numericDelta(d)) }}
                          </span>
                          <span v-else class="tw-text-orange-600 tw-font-bold">≠</span>
                        </div>
                      </template>
                    </div>
                  </div>
                </template>

                <!-- Missing: red cards -->
                <template v-else-if="currentTabKey === 'missing'">
                  <div class="card-grid">
                    <div
                      v-for="(row, idx) in currentTabRows"
                      :key="`miss-${idx}`"
                      class="info-card info-card-red"
                    >
                      <div class="info-card-badge">
                        <v-icon small color="red darken-2">mdi-minus-circle</v-icon>
                        <span>ขาดในระบบ</span>
                      </div>
                      <div class="info-card-docno">{{ row.upload.docId }}</div>
                      <div class="info-card-meta">
                        <div><v-icon x-small>mdi-calendar</v-icon> {{ formatDate(row.upload.docDate) }}</div>
                        <div><v-icon x-small>mdi-store</v-icon> {{ row.upload.cardName || '-' }}</div>
                        <div><v-icon x-small>mdi-card-account-details</v-icon> {{ row.upload.taxId || '-' }}</div>
                      </div>
                      <div class="info-card-amount">
                        <div>
                          <div class="info-amount-label">มูลค่าก่อน VAT</div>
                          <div class="info-amount-val">{{ formatNumber(row.upload.amount) }}</div>
                        </div>
                        <div>
                          <div class="info-amount-label">VAT</div>
                          <div class="info-amount-val">{{ formatNumber(row.upload.vatAmount) }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Extra: amber cards -->
                <template v-else-if="currentTabKey === 'extra'">
                  <div class="card-grid">
                    <div
                      v-for="(row, idx) in currentTabRows"
                      :key="`ex-${idx}`"
                      class="info-card info-card-amber"
                    >
                      <div class="info-card-badge">
                        <v-icon small color="amber darken-3">mdi-plus-circle</v-icon>
                        <span>เกินในระบบ</span>
                      </div>
                      <div class="info-card-docno">{{ row.system.docId }}</div>
                      <div class="info-card-meta">
                        <div><v-icon x-small>mdi-calendar</v-icon> {{ formatDate(row.system.docDate) }}</div>
                        <div><v-icon x-small>mdi-store</v-icon> {{ row.system.cardName || '-' }}</div>
                        <div><v-icon x-small>mdi-card-account-details</v-icon> {{ row.system.taxId || '-' }}</div>
                        <div v-if="row.system.status">
                          <v-icon x-small>mdi-tag</v-icon> {{ row.system.status }}
                        </div>
                      </div>
                      <div class="info-card-amount">
                        <div>
                          <div class="info-amount-label">มูลค่าก่อน VAT</div>
                          <div class="info-amount-val">{{ formatNumber(row.system.amount) }}</div>
                        </div>
                        <div>
                          <div class="info-amount-label">VAT</div>
                          <div class="info-amount-val">{{ formatNumber(row.system.vatAmount) }}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- All / Match: clean rounded table -->
                <template v-else>
                  <div class="rounded-table-wrap">
                    <v-simple-table>
                      <thead>
                        <tr>
                          <th>สถานะ</th>
                          <th>Doc No</th>
                          <th>วันที่</th>
                          <th>ผู้ขาย</th>
                          <th>TaxID</th>
                          <th>สาขา</th>
                          <th class="tw-text-right">มูลค่าก่อน VAT</th>
                          <th class="tw-text-right">VAT</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, idx) in currentTabRows"
                          :key="`r-${idx}`"
                          :class="rowClass(row)"
                        >
                          <td>
                            <v-chip x-small :color="statusChipColor(row.status)" text-color="white">
                              <v-icon x-small left>{{ statusIcon(row.status) }}</v-icon>
                              {{ statusLabel(row.status) }}
                            </v-chip>
                          </td>
                          <td class="tw-font-semibold">{{ pickShown(row, 'docId') }}</td>
                          <td>{{ formatDate(pickShown(row, 'docDate')) }}</td>
                          <td>{{ pickShown(row, 'cardName') }}</td>
                          <td>{{ pickShown(row, 'taxId') }}</td>
                          <td>{{ pickShown(row, 'branchCode') }}</td>
                          <td class="tw-text-right">{{ formatNumber(pickShown(row, 'amount')) }}</td>
                          <td class="tw-text-right">{{ formatNumber(pickShown(row, 'vatAmount')) }}</td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </div>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Snackbar -->
        <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
          {{ snackbar.text }}
        </v-snackbar>
      </v-container>
    </v-main>
  </Admin>
</template>

<script>
import Admin from "@/components/admin/admin-main-template";
import fc from "@/firebaseConfig";
import * as XLSX from "xlsx";
import moment from "moment";

const FIELD_LABELS = {
  docId: "เลขที่เอกสาร",
  docDate: "วันที่",
  cardName: "ชื่อผู้ขาย",
  taxId: "เลขผู้เสียภาษี",
  branchCode: "สาขา",
  amount: "มูลค่าก่อน VAT",
  vatAmount: "VAT",
};

const COMPARE_FIELDS = ["docDate", "cardName", "taxId", "branchCode", "amount", "vatAmount"];

const HEADER_ALIASES = {
  docId: ["เลขที่/เล่มที่", "เลขที่เอกสาร", "doc no", "docno", "doc id"],
  docDate: ["วัน เดือน ปี", "วันที่", "doc date", "date"],
  cardName: ["ชื่อผู้ซื้อสินค้า / ผู้รับบริการ", "ชื่อผู้ขาย", "ผู้ขาย", "vendor name", "vendor"],
  taxId: ["เลขประจำตัวผู้เสียภาษีอากร", "เลขผู้เสียภาษี", "tax id", "taxid"],
  branchCode: ["สถานประกอบการ", "สาขา", "branch", "branch code"],
  amount: ["มูลค่าสินค้าหรือบริการ", "มูลค่าก่อนภาษี", "amount", "amount before vat"],
  vatAmount: ["จำนวนเงินภาษีมูลค่าเพิ่ม", "ภาษีมูลค่าเพิ่ม", "vat", "vat amount"],
};

const KpiCard = {
  props: ["icon", "color", "value", "label"],
  template: `
    <div class="tw-border tw-shadow tw-border-gray-200 tw-rounded-xl tw-p-4 tw-flex tw-items-center tw-gap-3">
      <v-icon class="tw-p-2 tw-rounded-full" :class="'tw-bg-' + color + '-500'" color="white">{{ icon }}</v-icon>
      <div>
        <div class="tw-text-2xl tw-font-bold" :class="'tw-text-' + color + '-600'">{{ value }}</div>
        <div class="tw-text-xs tw-text-gray-500">{{ label }}</div>
      </div>
    </div>
  `,
};

export default {
  name: "documents.accountspayable.auditautomation",
  components: { Admin, KpiCard },
  data() {
    return {
      fileType: "excel",
      isDragging: false,
      isProcessing: false,
      processingStep: "",
      parseError: "",
      uploadRows: [],
      systemRows: [],
      monthlyRows: [],
      overall: {
        uploadCount: 0,
        systemCount: 0,
        matchCount: 0,
        mismatchCount: 0,
        missingCount: 0,
        extraCount: 0,
      },
      detailOpen: false,
      detailTab: 0,
      selectedMonth: null,
      detailRows: { all: [], match: [], mismatch: [], missing: [], extra: [] },
      snackbar: { show: false, color: "", text: "" },
    };
  },
  computed: {
    hasResult() {
      return this.monthlyRows.length > 0;
    },
    tabConfig() {
      return [
        { key: "all", label: "ทั้งหมด", icon: "mdi-format-list-bulleted", color: "blue-grey darken-1" },
        { key: "match", label: "Match", icon: "mdi-check-decagram", color: "green darken-1" },
        { key: "mismatch", label: "Mismatch", icon: "mdi-alert", color: "orange darken-2" },
        { key: "missing", label: "ขาดในระบบ", icon: "mdi-minus-circle", color: "red darken-2" },
        { key: "extra", label: "เกินในระบบ", icon: "mdi-plus-circle", color: "amber darken-3" },
      ];
    },
    currentTabKey() {
      return this.tabConfig[this.detailTab]?.key || "all";
    },
    currentTabRows() {
      return this.detailRows[this.currentTabKey] || [];
    },
    detailStats() {
      const r = this.detailRows;
      return {
        uploadCount: r.match.length + r.mismatch.length + r.missing.length,
        systemCount: r.match.length + r.mismatch.length + r.extra.length,
        matchCount: r.match.length,
        mismatchCount: r.mismatch.length,
        missingCount: r.missing.length,
        extraCount: r.extra.length,
      };
    },
    matchRate() {
      const total = this.detailStats.uploadCount;
      if (!total) return 0;
      return (this.detailStats.matchCount / total) * 100;
    },
    matchRateColor() {
      const r = this.matchRate;
      if (r >= 95) return "green darken-1";
      if (r >= 80) return "amber darken-1";
      return "red darken-2";
    },
    amountTotals() {
      const sum = (rows, side, field) =>
        rows.reduce((acc, r) => acc + Number((r[side] && r[side][field]) || 0), 0);
      const ups = [...this.detailRows.match, ...this.detailRows.mismatch, ...this.detailRows.missing];
      const sys = [...this.detailRows.match, ...this.detailRows.mismatch, ...this.detailRows.extra];
      const uploadAmount = sum(ups, "upload", "amount");
      const systemAmount = sum(sys, "system", "amount");
      const uploadVat = sum(ups, "upload", "vatAmount");
      const systemVat = sum(sys, "system", "vatAmount");
      return {
        uploadAmount,
        systemAmount,
        amountDiff: systemAmount - uploadAmount,
        uploadVat,
        systemVat,
        vatDiff: systemVat - uploadVat,
      };
    },
  },
  methods: {
    handleFilePick(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.startProcess(file);
      e.target.value = "";
    },
    handleDrop(e) {
      this.isDragging = false;
      const file = e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.startProcess(file);
    },
    async startProcess(file) {
      this.parseError = "";
      if (this.fileType === "pdf") {
        this.parseError = "ขณะนี้รองรับเฉพาะ Excel — PDF จะรองรับเมื่อพร้อม OCR";
        return;
      }
      this.isProcessing = true;
      try {
        this.processingStep = "กำลังอ่านไฟล์ Excel...";
        const rows = await this.parseExcel(file);
        if (rows.length === 0) throw new Error("ไม่พบข้อมูลในไฟล์");
        this.uploadRows = rows;

        this.processingStep = "กำลังดึงข้อมูลจากระบบ...";
        const months = this.getMonthsFromUpload(rows);
        const sysRows = await this.fetchSystemDocs(months);
        this.systemRows = sysRows;

        this.processingStep = "กำลังเทียบข้อมูล...";
        this.buildMonthlySummary();
      } catch (err) {
        console.error(err);
        this.parseError = err.message || "เกิดข้อผิดพลาดในการอ่านไฟล์";
        this.uploadRows = [];
        this.systemRows = [];
        this.monthlyRows = [];
      } finally {
        this.isProcessing = false;
        this.processingStep = "";
      }
    },
    parseExcel(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array", cellDates: true });
            const sheet = wb.Sheets[wb.SheetNames[0]];
            const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false, defval: "" });
            resolve(this.normalizeAoA(aoa));
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = () => reject(new Error("อ่านไฟล์ไม่สำเร็จ"));
        reader.readAsArrayBuffer(file);
      });
    },
    normalizeAoA(aoa) {
      const headerIdx = this.findHeaderRow(aoa);
      if (headerIdx < 0) throw new Error("ไม่พบหัวตารางในไฟล์ Excel — ตรวจสอบว่ามีคอลัมน์ 'เลขที่/เล่มที่' หรือ Doc No");
      const headers = aoa[headerIdx].map((h) => String(h || "").trim().toLowerCase());
      const colMap = {};
      Object.entries(HEADER_ALIASES).forEach(([key, aliases]) => {
        const idx = headers.findIndex((h) =>
          aliases.some((a) => h.includes(a.toLowerCase()))
        );
        if (idx >= 0) colMap[key] = idx;
      });
      if (colMap.docId === undefined) throw new Error("ไม่พบคอลัมน์ Doc No / เลขที่เอกสาร");

      const out = [];
      for (let i = headerIdx + 1; i < aoa.length; i++) {
        const r = aoa[i];
        if (!r || r.every((c) => c === "" || c == null)) continue;
        const docId = String(r[colMap.docId] || "").trim();
        if (!docId) continue;
        const lower = docId.toLowerCase();
        if (lower === "รวม" || lower.startsWith("total")) continue;
        out.push({
          docId,
          docDate: this.parseDate(r[colMap.docDate]),
          cardName: this.s(r[colMap.cardName]),
          taxId: this.s(r[colMap.taxId]),
          branchCode: this.s(r[colMap.branchCode]),
          amount: this.parseNumber(r[colMap.amount]),
          vatAmount: this.parseNumber(r[colMap.vatAmount]),
        });
      }
      return out;
    },
    findHeaderRow(aoa) {
      for (let i = 0; i < Math.min(aoa.length, 20); i++) {
        const joined = (aoa[i] || []).map((c) => String(c || "").toLowerCase()).join("|");
        if (HEADER_ALIASES.docId.some((a) => joined.includes(a.toLowerCase()))) return i;
      }
      return -1;
    },
    s(v) {
      return v == null ? "" : String(v).trim();
    },
    parseNumber(v) {
      if (v == null || v === "") return 0;
      const n = Number(String(v).replace(/,/g, ""));
      return Number.isFinite(n) ? n : 0;
    },
    parseDate(v) {
      if (!v) return null;
      if (v instanceof Date) return v;
      const s = String(v).trim();
      const fmts = ["D/M/YYYY", "DD/MM/YYYY", "D/M/YY", "YYYY-MM-DD", "M/D/YYYY"];
      for (const f of fmts) {
        const m = moment(s, f, true);
        if (m.isValid()) return m.toDate();
      }
      const m = moment(s);
      return m.isValid() ? m.toDate() : null;
    },
    monthKey(d) {
      if (!d) return "unknown";
      const m = d.toDate ? d.toDate() : d instanceof Date ? d : new Date(d);
      if (isNaN(m.getTime())) return "unknown";
      return moment(m).format("YYYY-MM");
    },
    getMonthsFromUpload(rows) {
      const set = new Set();
      rows.forEach((r) => set.add(this.monthKey(r.docDate)));
      return Array.from(set).filter((k) => k !== "unknown");
    },
    async fetchSystemDocs(months) {
      const companyID = this.$route.params.companyId;
      if (months.length === 0) return [];
      const ranges = months.map((m) => {
        const start = moment(m, "YYYY-MM").startOf("month").toDate();
        const end = moment(m, "YYYY-MM").endOf("month").toDate();
        return { start, end };
      });
      const minStart = new Date(Math.min(...ranges.map((r) => r.start.getTime())));
      const maxEnd = new Date(Math.max(...ranges.map((r) => r.end.getTime())));

      const snap = await fc.db
        .collection(`Companies/${companyID}/PurchaseInvoices`)
        .where("docDate", ">=", minStart)
        .where("docDate", "<=", maxEnd)
        .get();

      return snap.docs.map((d) => {
        const data = d.data();
        const docDate = data.docDate && data.docDate.toDate ? data.docDate.toDate() : data.docDate;
        return {
          id: d.id,
          docId: this.s(data.docId),
          docDate,
          cardName: this.s(data.cardName),
          taxId: this.s(data.taxId),
          branchCode: this.s(data.branchCode),
          amount: this.parseNumber(data.amount),
          vatAmount: this.parseNumber(data.vatAmount),
          status: data.status,
        };
      });
    },
    buildMonthlySummary() {
      const months = new Set();
      this.uploadRows.forEach((r) => months.add(this.monthKey(r.docDate)));
      this.systemRows.forEach((r) => months.add(this.monthKey(r.docDate)));

      const rows = [];
      let oUp = 0, oSys = 0, oM = 0, oMm = 0, oMiss = 0, oExtra = 0;

      Array.from(months)
        .filter((k) => k !== "unknown")
        .sort()
        .reverse()
        .forEach((month) => {
          const ups = this.uploadRows.filter((r) => this.monthKey(r.docDate) === month);
          const sys = this.systemRows.filter((r) => this.monthKey(r.docDate) === month);
          const sysIndex = new Map(sys.map((s) => [s.docId, s]));
          const upIndex = new Map(ups.map((u) => [u.docId, u]));

          let match = 0, mismatch = 0, missing = 0, extra = 0;
          ups.forEach((u) => {
            const s = sysIndex.get(u.docId);
            if (!s) missing++;
            else if (this.diffFields(u, s).length === 0) match++;
            else mismatch++;
          });
          sys.forEach((s) => {
            if (!upIndex.has(s.docId)) extra++;
          });

          rows.push({
            month,
            uploadCount: ups.length,
            systemCount: sys.length,
            matchCount: match,
            mismatchCount: mismatch,
            missingCount: missing,
            extraCount: extra,
          });

          oUp += ups.length;
          oSys += sys.length;
          oM += match;
          oMm += mismatch;
          oMiss += missing;
          oExtra += extra;
        });

      this.monthlyRows = rows;
      this.overall = {
        uploadCount: oUp,
        systemCount: oSys,
        matchCount: oM,
        mismatchCount: oMm,
        missingCount: oMiss,
        extraCount: oExtra,
      };
    },
    diffFields(up, sys) {
      const diffs = [];
      COMPARE_FIELDS.forEach((f) => {
        if (!this.fieldEqual(f, up[f], sys[f])) {
          diffs.push({ field: f, upload: up[f], system: sys[f] });
        }
      });
      return diffs;
    },
    fieldEqual(field, a, b) {
      if (field === "amount" || field === "vatAmount") {
        return Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.01;
      }
      if (field === "docDate") {
        const da = a ? moment(a).format("YYYY-MM-DD") : "";
        const db = b ? moment(b).format("YYYY-MM-DD") : "";
        return da === db;
      }
      return String(a || "").trim() === String(b || "").trim();
    },
    selectMonth(month) {
      this.selectedMonth = month;
      const ups = this.uploadRows.filter((r) => this.monthKey(r.docDate) === month);
      const sys = this.systemRows.filter((r) => this.monthKey(r.docDate) === month);
      const sysIndex = new Map(sys.map((s) => [s.docId, s]));
      const upIndex = new Map(ups.map((u) => [u.docId, u]));

      const match = [], mismatch = [], missing = [], extra = [];
      ups.forEach((u) => {
        const s = sysIndex.get(u.docId);
        if (!s) missing.push({ status: "missing", upload: u, system: null });
        else {
          const diffs = this.diffFields(u, s);
          if (diffs.length === 0) match.push({ status: "match", upload: u, system: s });
          else mismatch.push({ status: "mismatch", upload: u, system: s, diffs });
        }
      });
      sys.forEach((s) => {
        if (!upIndex.has(s.docId)) extra.push({ status: "extra", upload: null, system: s });
      });

      const all = [...mismatch, ...missing, ...extra, ...match];
      this.detailRows = { all, match, mismatch, missing, extra };
      this.detailTab = 0;
      this.detailOpen = true;
    },
    pickShown(row, field) {
      if (row.status === "extra") return row.system ? row.system[field] : "";
      return row.upload ? row.upload[field] : (row.system ? row.system[field] : "");
    },
    rowClass(row) {
      if (row.status === "mismatch") return "tw-bg-orange-50";
      if (row.status === "missing") return "tw-bg-red-50";
      if (row.status === "extra") return "tw-bg-yellow-50";
      return "";
    },
    statusChipColor(s) {
      return ({ match: "green", mismatch: "orange", missing: "red", extra: "amber darken-2" }[s] || "grey");
    },
    statusLabel(s) {
      return ({ match: "Match", mismatch: "Mismatch", missing: "ขาดในระบบ", extra: "เกินในระบบ" }[s] || s);
    },
    statusIcon(s) {
      return ({ match: "mdi-check", mismatch: "mdi-alert", missing: "mdi-minus-circle", extra: "mdi-plus-circle" }[s] || "mdi-help");
    },
    isNumericField(f) {
      return f === "amount" || f === "vatAmount";
    },
    numericDelta(d) {
      return Number(d.system || 0) - Number(d.upload || 0);
    },
    deltaClass(v) {
      if (Math.abs(v) < 0.01) return "delta-zero";
      return v > 0 ? "delta-pos" : "delta-neg";
    },
    deltaIcon(v) {
      if (Math.abs(v) < 0.01) return "mdi-equal";
      return v > 0 ? "mdi-arrow-up-bold" : "mdi-arrow-down-bold";
    },
    formatNumberSigned(v) {
      if (Math.abs(v) < 0.01) return "0.00";
      const n = Number(v);
      const sign = n > 0 ? "+" : "-";
      return sign + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    monthStatusColor(m) {
      if (m.mismatchCount + m.missingCount + m.extraCount === 0) return "green";
      if (m.mismatchCount + m.missingCount + m.extraCount <= 2) return "orange";
      return "red";
    },
    monthStatusLabel(m) {
      const issues = m.mismatchCount + m.missingCount + m.extraCount;
      if (issues === 0) return "ตรงทั้งหมด";
      return `${issues} รายการ`;
    },
    formatMonth(m) {
      if (!m || m === "unknown") return "ไม่ระบุเดือน";
      return moment(m, "YYYY-MM").format("MMMM YYYY");
    },
    formatDate(v) {
      if (!v) return "-";
      const m = moment(v);
      return m.isValid() ? m.format("DD/MM/YYYY") : "-";
    },
    formatNumber(v) {
      if (v == null || v === "") return "-";
      const n = Number(v);
      if (!Number.isFinite(n)) return "-";
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatField(field, v) {
      if (field === "docDate") return this.formatDate(v);
      if (field === "amount" || field === "vatAmount") return this.formatNumber(v);
      return v == null || v === "" ? "-" : v;
    },
    fieldLabel(f) {
      return FIELD_LABELS[f] || f;
    },
    resetAll() {
      this.uploadRows = [];
      this.systemRows = [];
      this.monthlyRows = [];
      this.overall = {
        uploadCount: 0, systemCount: 0, matchCount: 0,
        mismatchCount: 0, missingCount: 0, extraCount: 0,
      };
      this.parseError = "";
    },
    buildDetailSheet(months) {
      const header = [
        "Month", "Status", "Doc No", "Doc Date",
        "Vendor (Upload)", "Vendor (System)",
        "TaxID (Upload)", "TaxID (System)",
        "Branch (Upload)", "Branch (System)",
        "Amount (Upload)", "Amount (System)",
        "VAT (Upload)", "VAT (System)",
        "Diff Fields",
      ];
      const rows = [header];
      months.forEach((m) => {
        const ups = this.uploadRows.filter((r) => this.monthKey(r.docDate) === m.month);
        const sys = this.systemRows.filter((r) => this.monthKey(r.docDate) === m.month);
        const sysIndex = new Map(sys.map((s) => [s.docId, s]));
        const upIndex = new Map(ups.map((u) => [u.docId, u]));
        ups.forEach((u) => {
          const s = sysIndex.get(u.docId);
          let status, diffs = [];
          if (!s) status = "Missing";
          else {
            diffs = this.diffFields(u, s);
            status = diffs.length === 0 ? "Match" : "Mismatch";
          }
          rows.push([
            this.formatMonth(m.month), status, u.docId,
            this.formatDate(u.docDate),
            u.cardName, s ? s.cardName : "",
            u.taxId, s ? s.taxId : "",
            u.branchCode, s ? s.branchCode : "",
            u.amount, s ? s.amount : "",
            u.vatAmount, s ? s.vatAmount : "",
            diffs.map((d) => this.fieldLabel(d.field)).join(", "),
          ]);
        });
        sys.forEach((s) => {
          if (upIndex.has(s.docId)) return;
          rows.push([
            this.formatMonth(m.month), "Extra", s.docId,
            this.formatDate(s.docDate),
            "", s.cardName,
            "", s.taxId,
            "", s.branchCode,
            "", s.amount,
            "", s.vatAmount,
            "",
          ]);
        });
      });
      return rows;
    },
    exportMonth(month) {
      const m = this.monthlyRows.find((x) => x.month === month);
      if (!m) return;
      const wb = XLSX.utils.book_new();
      const summary = [
        [`Audit Automation - ${this.formatMonth(month)}`, ""],
        ["Generated", moment().format("YYYY-MM-DD HH:mm")],
        [],
        ["Upload", m.uploadCount],
        ["System", m.systemCount],
        ["Match", m.matchCount],
        ["Mismatch", m.mismatchCount],
        ["Missing in System", m.missingCount],
        ["Extra in System", m.extraCount],
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(this.buildDetailSheet([m])), "Details");
      const filename = `audit-${month}-${moment().format("YYYYMMDD-HHmm")}.xlsx`;
      XLSX.writeFile(wb, filename);
      this.snackbar = { show: true, color: "success", text: `Export ${filename} สำเร็จ` };
    },
    exportExcel() {
      const wb = XLSX.utils.book_new();
      const summary = [
        ["Audit Automation - Summary", ""],
        ["Generated", moment().format("YYYY-MM-DD HH:mm")],
        [],
        ["Upload", this.overall.uploadCount],
        ["System", this.overall.systemCount],
        ["Match", this.overall.matchCount],
        ["Mismatch", this.overall.mismatchCount],
        ["Missing in System", this.overall.missingCount],
        ["Extra in System", this.overall.extraCount],
        [],
        ["Per-Month Breakdown"],
        ["Month", "Upload", "System", "Match", "Mismatch", "Missing", "Extra"],
        ...this.monthlyRows.map((m) => [
          this.formatMonth(m.month),
          m.uploadCount, m.systemCount, m.matchCount,
          m.mismatchCount, m.missingCount, m.extraCount,
        ]),
      ];
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(this.buildDetailSheet(this.monthlyRows)), "Details");
      const filename = `audit-automation-${moment().format("YYYYMMDD-HHmm")}.xlsx`;
      XLSX.writeFile(wb, filename);
      this.snackbar = { show: true, color: "success", text: `Export ${filename} สำเร็จ` };
    },
  },
};
</script>

<style scoped lang="scss">
.wrapper {
  background: white;
}

/* ===== Detail Dashboard ===== */
.detail-dashboard {
  background: #f7f8fc;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 28px;
  background: linear-gradient(135deg, #4338ca 0%, #6366f1 50%, #8b5cf6 100%);
  color: white;

  &-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .detail-month {
    font-size: 22px;
    font-weight: 700;
    line-height: 1.2;
  }

  .detail-subtitle {
    font-size: 12px;
    opacity: 0.85;
    margin-top: 2px;
  }
}

/* Hero stats */
.hero-stats {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 24px;
  padding: 24px 28px;
  background: white;
  border-bottom: 1px solid #eef0f5;
}

.hero-donut {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;

  .hero-rate-num {
    font-size: 22px;
    font-weight: 800;
    color: #1f2937;
    line-height: 1;
  }
  .hero-rate-label {
    font-size: 11px;
    color: #6b7280;
    margin-top: 4px;
  }
  .hero-rate-caption {
    font-size: 12px;
    color: #6b7280;
  }
}

.hero-tiles {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: 1fr 1fr;
  gap: 12px;
}

.stat-tile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #eef0f5;
  background: white;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }

  .stat-icon-wrap {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .stat-num {
    font-size: 22px;
    font-weight: 800;
    line-height: 1;
    color: #111827;
  }
  .stat-label {
    font-size: 11px;
    color: #6b7280;
    margin-top: 4px;
  }
}
.stat-blue   .stat-icon-wrap { background: #3b82f6; }
.stat-indigo .stat-icon-wrap { background: #6366f1; }
.stat-green  .stat-icon-wrap { background: #10b981; }
.stat-orange .stat-icon-wrap { background: #f59e0b; }
.stat-red    .stat-icon-wrap { background: #ef4444; }
.stat-amber  .stat-icon-wrap { background: #d97706; }

/* Amount reconciliation */
.amount-reconcile {
  display: flex;
  gap: 24px;
  padding: 18px 28px;
  background: white;
  border-bottom: 1px solid #eef0f5;
}

.amount-block {
  flex: 1;

  &-head {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
}

.amount-pair {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.amount-side {
  &-label {
    font-size: 10px;
    color: #9ca3af;
    text-transform: uppercase;
  }
  &-val {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    font-variant-numeric: tabular-nums;
  }
}

.amount-arrow {
  color: #cbd5e1;
}

.amount-delta {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;

  &.delta-zero { background: #ecfdf5; color: #047857; }
  &.delta-pos  { background: #fef3c7; color: #b45309; }
  &.delta-neg  { background: #fee2e2; color: #b91c1c; }
}

/* Filter pills */
.filter-pills {
  padding: 16px 28px 0;
  background: #f7f8fc;
}

.filter-chip-count {
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.25);
}
.filter-chip.v-chip--outlined .filter-chip-count {
  background: rgba(0, 0, 0, 0.06);
}

/* Body */
.detail-body {
  padding: 16px 28px 28px;
  background: #f7f8fc;
  min-height: 240px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

/* Mismatch comparison cards */
.mismatch-card {
  background: white;
  border: 1px solid #fde68a;
  border-left: 4px solid #f59e0b;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
    flex-wrap: wrap;
    gap: 8px;

    .mismatch-docno {
      font-size: 16px;
      font-weight: 700;
      color: #111827;
      letter-spacing: 0.02em;
    }
  }
}

.diff-grid {
  display: grid;
  grid-template-columns: 160px 1fr 1fr 120px;
  gap: 0;
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  overflow: hidden;

  .diff-head {
    background: #f8fafc;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #e2e8f0;
  }
  .diff-head-empty {}
  .diff-head-upload  { color: #1d4ed8; }
  .diff-head-system  { color: #4f46e5; }
  .diff-head-delta   { color: #b45309; text-align: right; }

  .diff-cell {
    padding: 10px 12px;
    font-size: 13px;
    border-bottom: 1px solid #f1f5f9;

    &-label {
      background: #fafbff;
      color: #1f2937;
      font-weight: 600;
    }
    &-upload {
      background: #eff6ff;
      color: #1e3a8a;
      font-variant-numeric: tabular-nums;
    }
    &-system {
      background: #fef2f2;
      color: #991b1b;
      font-variant-numeric: tabular-nums;
    }
    &-delta {
      text-align: right;
      font-variant-numeric: tabular-nums;
      font-weight: 700;

      .delta-pos { color: #b45309; }
      .delta-neg { color: #b91c1c; }
      .delta-zero { color: #047857; }
    }
  }

  /* Last row removes bottom border */
  > .diff-cell:nth-last-child(-n+4) {
    border-bottom: none;
  }
}

/* Info cards (missing / extra) */
.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

.info-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #e5e7eb;
  border-top: 4px solid #cbd5e1;

  &-red    { border-top-color: #ef4444; }
  &-amber  { border-top-color: #d97706; }

  &-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 999px;
    background: #f3f4f6;
    color: #4b5563;
    margin-bottom: 8px;
  }
  &-red &-badge   { background: #fee2e2; color: #b91c1c; }
  &-amber &-badge { background: #fef3c7; color: #92400e; }

  &-docno {
    font-size: 18px;
    font-weight: 800;
    color: #111827;
    margin-bottom: 8px;
  }
  &-meta {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 12px;

    > div {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 2px 0;
    }
  }
  &-amount {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    border-top: 1px dashed #e5e7eb;
    padding-top: 10px;

    .info-amount-label {
      font-size: 10px;
      color: #9ca3af;
      text-transform: uppercase;
    }
    .info-amount-val {
      font-size: 14px;
      font-weight: 700;
      color: #111827;
      font-variant-numeric: tabular-nums;
    }
  }
}

/* Rounded table for All / Match */
.rounded-table-wrap {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #eef0f5;
}
.rounded-table-wrap ::v-deep table {
  thead th {
    background: #f8fafc;
    color: #475569;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  tbody tr:hover {
    background: #f8fafc;
  }
}
</style>
